import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { Link2, Plug, RefreshCcw, RotateCw, SplitSquareHorizontal, Unplug, Users } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import { ApiError } from '../../lib/api';
import { useCurrentUser } from '../auth';
import type { XeroTenantConnection } from '../../types/xero';
import {
  disconnectXeroTenant,
  getXeroConnections,
  startXeroConnect,
  syncXeroTenant,
} from './api';

const CONNECTIONS_KEY = ['xero', 'connections'] as const;

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

// Admin-only "Xero connections" view. Connect / reconnect each org (AU + NZ) and
// see each connection's last-sync status. The revenue sync itself is triggered
// separately (chunk 1b).
export function XeroConnectionsPage() {
  const me = useCurrentUser();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();

  const connections = useQuery({
    queryKey: CONNECTIONS_KEY,
    queryFn: getXeroConnections,
    enabled: me.role === 'admin',
  });

  // Surface the OAuth callback's redirect result once, then clear the params.
  useEffect(() => {
    const connected = params.get('connected');
    const error = params.get('error');
    if (connected) {
      const n = Number(connected);
      toast.success(`Connected ${n} Xero organisation${n === 1 ? '' : 's'}.`);
    } else if (error) {
      toast.error(error);
    }
    if (connected || error) {
      const next = new URLSearchParams(params);
      next.delete('connected');
      next.delete('error');
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = useMutation({
    mutationFn: startXeroConnect,
    onSuccess: ({ authUrl }) => {
      // Full-tab navigation — Xero's consent screen redirects back to
      // /settings/xero when done.
      window.location.href = authUrl;
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Couldn't start the Xero connect."),
  });

  const disconnect = useMutation({
    mutationFn: disconnectXeroTenant,
    onSuccess: () => {
      toast.success('Disconnected. Synced revenue is retained.');
      void qc.invalidateQueries({ queryKey: CONNECTIONS_KEY });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Couldn't disconnect."),
  });

  const sync = useMutation({
    mutationFn: syncXeroTenant,
    onSuccess: ({ result }) => {
      if (result.ok) {
        const unattributed =
          result.unattributedLineCount > 0
            ? ` · ${result.unattributedLineCount} unmapped line${result.unattributedLineCount === 1 ? '' : 's'}`
            : '';
        const skipped =
          result.skippedEmptyIdCount > 0
            ? ` · ${result.skippedEmptyIdCount} skipped (no id)`
            : '';
        toast.success(
          `Synced ${result.tenantName ?? 'org'}: ${result.invoicesPulled} invoice${result.invoicesPulled === 1 ? '' : 's'}${unattributed}${skipped}.`,
        );
      } else if (result.rateLimitKind === 'day') {
        // The daily cap can't be waited out in-request — progress is saved.
        toast.warning('Xero daily API limit reached — retry tomorrow. Progress so far was saved.');
      } else if (result.rateLimitKind === 'minute') {
        toast.warning('Xero rate limit hit — try again in a minute. Progress so far was saved.');
      } else {
        toast.error(`Sync failed: ${result.error ?? 'unknown error'}`);
      }
      void qc.invalidateQueries({ queryKey: CONNECTIONS_KEY });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Couldn't sync."),
  });

  if (me.role !== 'admin') {
    return <p className="text-sm text-slate-600">Only admins can manage Xero connections.</p>;
  }

  const data = connections.data;
  const configured = data?.configured ?? false;
  const rows = data?.connections ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Xero connections"
        description="Connect each Xero organisation (AU + NZ) so invoiced revenue flows into Account Analytics."
        backHref="/settings"
        actions={
          <>
            <Button asChild variant="outline" disabled={rows.length === 0}>
              <Link to="/settings/xero/mapping">
                <Users className="h-4 w-4" />
                Map contacts
              </Link>
            </Button>
            <Button asChild variant="outline" disabled={rows.length === 0}>
              <Link to="/settings/xero/allocations">
                <SplitSquareHorizontal className="h-4 w-4" />
                Grouped invoices
              </Link>
            </Button>
            <Button
              onClick={() => connect.mutate()}
              disabled={!configured || connect.isPending}
            >
              <Plug className="h-4 w-4" />
              Connect an organisation
            </Button>
          </>
        }
      />

      {connections.isLoading ? (
        <CenteredSpinner />
      ) : connections.error ? (
        <p className="text-sm text-red-600">Couldn't load Xero connections. Try refreshing.</p>
      ) : !configured ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          Xero is not configured. Set <code>XERO_CLIENT_ID</code> and{' '}
          <code>XERO_CLIENT_SECRET</code> (Secret Manager → Cloud Run) and redeploy, then connect an
          organisation.
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-slate-200 bg-white p-8 text-center">
          <Link2 className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-2 text-sm font-medium text-slate-700">No organisations connected yet.</p>
          <p className="mt-0.5 text-sm text-slate-500">
            Connect the AU org, then the NZ org — each is a separate authorization.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2.5">Organisation</th>
                <th className="px-4 py-2.5">Last sync</th>
                <th className="px-4 py-2.5">Connected by</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((c) => (
                <ConnectionRow
                  key={c.tenantId}
                  conn={c}
                  onSync={() => sync.mutate(c.tenantId)}
                  syncing={sync.isPending && sync.variables === c.tenantId}
                  onReconnect={() => connect.mutate()}
                  onDisconnect={() => {
                    if (
                      window.confirm(
                        `Disconnect "${c.tenantName ?? c.tenantId}"? Synced revenue is kept; you'll need to reconnect to sync again.`,
                      )
                    ) {
                      disconnect.mutate(c.tenantId);
                    }
                  }}
                  busy={connect.isPending || disconnect.isPending || sync.isPending}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SyncBadge({ conn }: { conn: XeroTenantConnection }) {
  const map: Record<XeroTenantConnection['lastSyncStatus'], string> = {
    never: 'bg-slate-100 text-slate-600',
    success: 'bg-kyck-50 text-kyck-700',
    error: 'bg-red-50 text-red-700',
  };
  const label =
    conn.lastSyncStatus === 'never'
      ? 'Never synced'
      : conn.lastSyncStatus === 'success'
        ? `Synced ${formatDateTime(conn.lastSyncAt)}`
        : 'Last sync failed';
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${map[conn.lastSyncStatus]}`}>
      {label}
    </span>
  );
}

function ConnectionRow({
  conn,
  onSync,
  syncing,
  onReconnect,
  onDisconnect,
  busy,
}: {
  conn: XeroTenantConnection;
  onSync: () => void;
  syncing: boolean;
  onReconnect: () => void;
  onDisconnect: () => void;
  busy: boolean;
}) {
  return (
    <tr>
      <td className="px-4 py-3">
        <div className="font-medium text-slate-900">{conn.tenantName ?? conn.tenantId}</div>
        <div className="text-xs text-slate-500">Token valid until {formatDateTime(conn.tokenExpiresAt)}</div>
      </td>
      <td className="px-4 py-3">
        <SyncBadge conn={conn} />
        {conn.lastSyncStatus === 'error' && conn.lastSyncError && (
          <div className="mt-1 max-w-xs truncate text-xs text-red-600" title={conn.lastSyncError}>
            {conn.lastSyncError}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-slate-600">{conn.connectedByEmail}</td>
      <td className="px-4 py-3">
        <div className="flex justify-end gap-2">
          <Button size="sm" onClick={onSync} disabled={busy}>
            <RotateCw className={`h-3.5 w-3.5${syncing ? ' animate-spin' : ''}`} />
            {syncing ? 'Syncing…' : 'Sync now'}
          </Button>
          <Button variant="outline" size="sm" onClick={onReconnect} disabled={busy}>
            <RefreshCcw className="h-3.5 w-3.5" />
            Reconnect
          </Button>
          <Button variant="ghost" size="sm" onClick={onDisconnect} disabled={busy}>
            <Unplug className="h-3.5 w-3.5" />
            Disconnect
          </Button>
        </div>
      </td>
    </tr>
  );
}
